<script setup lang="ts">
  import { computed, type Component } from 'vue';
  import { IconImage, Checkbox, Tooltip, InfoTooltip } from '@/shared/ui';
  import { KeyRound } from 'lucide-vue-next';
  import PencilIcon from '@/shared/assets/icons/PencilGray.svg';
  import Trash from '@/shared/assets/icons/TrashIcon.svg';
  import InfoIcon from '@/shared/assets/icons/InfoIcon.svg';
  import type { AdminUser } from '../model';
  import { formatCivilDateToPtBR } from '@/shared/lib';
  import { useUserStore } from '@/entities/User';
  import { formatMemberAuditDateTime } from '../lib/member-audit-datetime';
  const props = defineProps<{
    user: AdminUser;
    isSelected: boolean;
  }>();

  const emit = defineEmits<{
    'toggle-selection': [id: number];
    edit: [id: number];
    info: [id: number];
    delete: [id: number];
    'reset-password': [id: number];
  }>();

  const toggleUser = (id: number) => {
    emit('toggle-selection', id);
  };

  const handleEdit = () => {
    emit('edit', props.user.id);
  };

  const handleInfo = () => {
    emit('info', props.user.id);
  };

  const handleDelete = () => {
    if (isCurrentUser.value) return;
    emit('delete', props.user.id);
  };

  const handleResetPassword = () => {
    if (isCurrentUser.value) return;
    emit('reset-password', props.user.id);
  };

  const userStore = useUserStore();
  const isCurrentUser = computed(() => {
    return props.user.id === userStore.id;
  });

  const formattedCreatedAt = computed(() => formatMemberAuditDateTime(props.user.createdAt));
  const formattedUpdatedAt = computed(() => formatMemberAuditDateTime(props.user.updatedAt));

  const formattedAdmissionDate = computed(() => formatCivilDateToPtBR(props.user.admissionDate));

  const formattedDismissalDate = computed(() =>
    props.user.dismissalDate ? formatCivilDateToPtBR(props.user.dismissalDate) : '-'
  );

  const statusLabel = computed(() =>
    props.user.status?.toUpperCase() === 'ACTIVE' ? 'Ativo' : 'Inativo'
  );
  const statusClass = computed(() =>
    props.user.status?.toUpperCase() === 'ACTIVE'
      ? 'px-3 py-1 rounded-full text-xs/3.5 font-medium inline-block bg-green-20 text-gray-07'
      : 'px-3 py-1 rounded-full text-xs/3.5 font-medium inline-block bg-pink-11 text-red-16'
  );

  interface ActionItem {
    icon: string | Component;
    tooltip: string;
    handler: () => void;
    disabled: boolean;
    isLucideIcon?: boolean;
  }

  const actions = computed<ActionItem[]>(() => [
    {
      icon: PencilIcon,
      tooltip: 'Editar',
      handler: handleEdit,
      disabled: false,
    },
    {
      icon: KeyRound,
      tooltip: 'Redefinir Senha',
      handler: handleResetPassword,
      disabled: isCurrentUser.value,
      isLucideIcon: true,
    },
    {
      icon: Trash,
      tooltip: 'Deletar',
      handler: handleDelete,
      disabled: isCurrentUser.value,
    },
  ]);
</script>

<template>
  <tr class="table-row-hover transition-colors duration-300 ease-out">
    <td class="w-20 align-middle text-center">
      <Checkbox
        :id="user.id"
        :checked="isSelected"
        @toggle="toggleUser(user.id)"
      />
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ user.name }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ user.registration }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-left">
      <span :class="statusClass">
        {{ statusLabel }}
      </span>
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formattedAdmissionDate }}
    </td>
    <td class="pl-6 py-4.5 text-sm text-gray-07 text-left">
      {{ formattedDismissalDate }}
    </td>
    <td class="pl-6 py-4 text-sm text-gray-07 w-50">
      <div class="flex gap-2 text-gray-xx-02 items-center">
        <template
          v-for="action in actions"
          :key="action.tooltip"
        >
          <Tooltip
            v-if="!action.disabled"
            :content="action.tooltip"
            placement="top"
            class="flex items-center"
          >
            <button
              class="cursor-pointer"
              @click="action.handler()"
            >
              <component
                :is="action.icon"
                v-if="action.isLucideIcon"
                :size="19"
                class="text-gray-xx-02"
              />
              <IconImage
                v-else
                :src="action.icon as string"
                :alt="action.tooltip"
              />
            </button>
          </Tooltip>
          <button
            v-else
            disabled
            class="flex items-center opacity-50 cursor-not-allowed"
          >
            <component
              :is="action.icon"
              v-if="action.isLucideIcon"
              :size="19"
              class="text-gray-xx-02"
            />
            <IconImage
              v-else
              :src="action.icon as string"
              :alt="action.tooltip"
            />
          </button>
        </template>
        <InfoTooltip
          :icon="InfoIcon"
          title="Informações"
          :created-at="formattedCreatedAt"
          :created-by="user.creator?.name"
          :updated-at="formattedUpdatedAt"
          :updated-by="user.updater?.name"
          placement="top"
        >
          <button @click="handleInfo">
            <IconImage
              :src="InfoIcon"
              alt="Informações"
            />
          </button>
        </InfoTooltip>
      </div>
    </td>
  </tr>
</template>
